import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useCreditClient } from '../../../../services/credit';
import SubheadingWithDivider from '../UI/SubheadingWithDivider';
import DropDownList, {
  DropDownListOption
} from '../../../../components/UI/DropDownList';
import Checkbox from '../../../../components/UI/Checkbox';
import Cleave from '../../../../components/UI/CustomInputField/CustomCleaveField';
import FieldLabel from '../UI/FieldLabel';
import css from './IncomeInformation.module.scss';
import Button from '../../../../components/UI/Button';
import Modal from '../UI/Modal';
import { AnalyticsContext } from '../../../../ModularReduxAnalytics';

export default function IncomeInformation({
  updateCreditApplication,
  authToken,
  handleCreditError,
  clearError
}) {
  const creditApplicationClient = useMemo(() => useCreditClient(), []);
  const [employmentStatus, setEmploymentStatus] = useState<string>();
  const [yearlyIncome, setYearlyIncome] = useState<string>();
  const [nonTaxableIncome, setNonTaxableIncome] = useState<boolean>(false);
  const [displayIncomeInfoModal, setDisplayIncomeInfoModal] =
    useState<boolean>(false);
  const [displayNonTaxableInfoModal, setDisplayNonTaxableInfoModal] =
    useState<boolean>(false);
  const { analytics } = useContext(AnalyticsContext);

  const employmentOptions: DropDownListOption[] = [
    { label: 'Employed', value: 'employed' },
    { label: 'Self-Employed', value: 'self_employed' },
    { label: 'Unemployed', value: 'unemployed' },
    { label: 'Other', value: 'other' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.page('Registration', 'Credit Application Income Information');
  }, []);

  const handleNextClick = async () => {
    if (!employmentStatus) {
      handleCreditError('Please select an employment status.');
    } else if (!yearlyIncome) {
      handleCreditError('Please enter your yearly income amount.');
    } else {
      try {
        await creditApplicationClient.submitEmploymentInformation({
          authToken,
          yearlyIncome: parseFloat(yearlyIncome),
          employmentStatus,
          nonTaxableYearlyIncome: nonTaxableIncome
        });
        clearError();
        updateCreditApplication();
      } catch {
        handleCreditError();
      }
    }
  };

  return (
    <>
      {displayIncomeInfoModal && (
        <Modal
          header="Total yearly income"
          subheader="Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation. In addition to your income (e.g., salary, wages, bonus pay, tips, commissions, retirement, etc.), if you are at least 21 years old you may include income of others that you can reasonably expect to access (e.g.,that portion of income of another person regularly deposited into your individual account or joint account you share with that person; or that portion of income of another person which regularly used to pay for your expenses)."
          option="Ok"
          optionAction={() => setDisplayIncomeInfoModal(false)}
          secondaryOption={undefined}
          secondaryOptionAction={undefined}
        />
      )}
      {displayNonTaxableInfoModal && (
        <Modal
          header="Non-taxable income"
          subheader="This is the portion of your total annual income that isn't subject to income tax. Examples of income that are typically not subject to income tax may include money you receive from disability, worker's compensation, child support, housing allowance, social security, or public assistance."
          option="Ok"
          optionAction={() => setDisplayNonTaxableInfoModal(false)}
          secondaryOption={undefined}
          secondaryOptionAction={undefined}
        />
      )}
      <SubheadingWithDivider />
      <h1>Income information</h1>
      <SubheadingWithDivider heading="Please tell us about what you earn in a year" />
      <div className={css.employmentStatusContainer}>
        <FieldLabel labelText="What is your employment status?" />
        <div className={css.employmentStatusInput}>
          <DropDownList
            options={employmentOptions}
            placeholder="Select an option"
            onChange={val => {
              setEmploymentStatus(val);
            }}
          />
        </div>
      </div>
      <div className={css.incomeContainer}>
        <Cleave
          placeholder="What is your yearly income?"
          options={{
            prefix: '$',
            rawValueTrimPrefix: true,
            numeral: true,
            numeralThousandsGroupStyle: 'thousand',
            numeralDecimalScale: 2,
            numeralPositiveOnly: true
          }}
          onChange={val => {
            setYearlyIncome(val);
          }}
          value={yearlyIncome}
          data-e2e="income-information"
          data-testid="income-information"
          inputMode="numeric"
          pattern="[0-9\.]*"
          rawValue={true}
          allowFullStory={false}
        />
        <div className={css.iconContainer}>
          <img
            src="/static/informational-icon.svg"
            onClick={() => setDisplayIncomeInfoModal(true)}
            data-testid="more-income-info-icon"
          />
        </div>
      </div>
      <div className={css.nonTaxableIncomeContainer}>
        <Checkbox
          id="non-taxable-income"
          checked={nonTaxableIncome}
          onChange={() => {
            setNonTaxableIncome(!nonTaxableIncome);
          }}
          data-e2e="non-taxable-income"
        />
        <div className={css.labelContainer}>
          <FieldLabel labelText="Is any portion of your yearly income non-taxable?" />
          <p>
            Alimony, child support, or separate maintenance income do not need
            to be revealed if you do not wish to have it considered as a basis
            for repaying this obligation.
          </p>
        </div>
        <div className={css.iconContainer}>
          <img
            src="/static/informational-icon.svg"
            onClick={() => setDisplayNonTaxableInfoModal(true)}
            data-testid="more-nontaxable-info-icon"
          />
        </div>
      </div>
      <Button text="Next" onClick={handleNextClick} />
    </>
  );
}
